import { useCallback, useEffect, useState } from "react";

export type Appearance = "light" | "dark" | "system";

const prefersDark = () => {
  if (typeof window === "undefined") {
    return false;
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
};

const applyTheme = (appearance: Appearance) => {
  if (typeof document === "undefined") return;

  const isDark = appearance === "dark" || (appearance === "system" && prefersDark());

  document.documentElement.classList.toggle("dark", isDark);
};

const mediaQuery = () => {
  if (typeof window === "undefined") {
    return null;
  }

  return window.matchMedia("(prefers-color-scheme: dark)");
};

const handleSystemThemeChange = () => {
  if (typeof localStorage === "undefined") return;

  const currentAppearance = localStorage.getItem("appearance") as Appearance;
  applyTheme(currentAppearance ?? "system");
};

export function initializeTheme() {
  if (typeof localStorage === "undefined") return;

  const savedAppearance = (localStorage.getItem("appearance") as Appearance) || "system";

  applyTheme(savedAppearance);

  mediaQuery()?.addEventListener("change", handleSystemThemeChange);
}

export function useAppearance() {
  const [appearance, setAppearance] = useState<Appearance>("system");

  const updateAppearance = useCallback((mode: Appearance) => {
    setAppearance(mode);
    if (mode === "system") {
      localStorage.removeItem("appearance");
    } else {
      localStorage.setItem("appearance", mode);
    }
    applyTheme(mode);
  }, []);

  useEffect(() => {
    const savedAppearance = localStorage.getItem("appearance") as Appearance | null;
    updateAppearance(savedAppearance ?? "system");

    return () => mediaQuery()?.removeEventListener("change", handleSystemThemeChange);
  }, [updateAppearance]);

  return { appearance, updateAppearance } as const;
}
